/*
<purpose>
  Sender-identity registry (P2, 2026-09-13), Task R2 — THE facets read for both ticket-list pages
  (AntwortenSeite's active buckets, ArchivSeite's archive buckets). Owns the debounce, the
  out-of-order guard and the unsupported-backend flag that both pages spelled twice, line for line,
  before the final review (item 4): one effect, one posture, one place to fix the next defect.
</purpose>
<dependencies>getTicketFacets (services/api.ts) — the ONE `/tickets/facets` endpoint, which is why
  this hook is named for tickets and not for either list.</dependencies>
<constraints>
  - 300ms DEBOUNCE: a clerk typing a search term fires one facets request, not one per keystroke.
  - ORDERING: every fired request carries a sequence number; a response whose sequence is not the
    newest fired is DROPPED. Without it a slow early response lands after a fast later one and the
    bubbles state counts for a filter set the clerk has already left (measured possible: the two
    requests differ only in query string, so nothing else distinguishes them).
  - `facets` is NEVER cleared mid-fetch: the last successful read stays on screen while the next is
    in flight, so a count can't flicker to its loading placeholder on every filter tweak (UI Layout
    Stability).
  - UNSUPPORTED BACKEND: getTicketFacets answers null on ANY failure (404 included — a bundle
    running against a core that predates /tickets/facets). A null with NO successful read behind it
    means this backend does not serve facets: `facetsUnsupported` latches, the callers render no
    count spans at all, and this hook stops fetching for its lifetime. Two reasons, both measured on
    the pre-fix pages: the bubbles otherwise carry a permanent „–" (a count that will never arrive,
    read by a clerk as "still loading"), and every filter tweak fires another doomed request that
    logs another console error. Latching makes that AT MOST ONE reflow; a page remount retries,
    which is the honest retry boundary — nothing about a 404 gets better within one mounted list.
    A null AFTER a successful read is a DIFFERENT fact — a transient failure against a backend that
    demonstrably serves this route — and is handled by doing nothing at all: the last successful
    counts stay on screen (never cleared) and the next filter change tries again. Latching there
    would hide every count for the session over one blip.
  - The request key is the SERIALIZED filter set, not a dependency array: a caller building the
    filters object inline (both pages do) would otherwise re-fire the effect on every render, and a
    hand-maintained dep array per caller is exactly the drift this extraction removes.
</constraints>
*/
import { useEffect, useRef, useState } from 'react';
import { getTicketFacets, type TicketFacets } from '../services/api';

/** Every filter the facets read takes EXCEPT the bucket — derived from the api function itself, so
 *  a param added there reaches both pages without a second declaration to keep in step. */
export type TicketFacetFilters = Omit<Parameters<typeof getTicketFacets>[0], 'bucket'>;

export interface TicketFacetsState {
  /** The last SUCCESSFUL read, or null before the first one lands. Never cleared by a later failure. */
  facets: TicketFacets | null;
  /** True once the FIRST fetch has failed with nothing behind it — the caller then renders no
   *  count spans at all rather than a „–" that will never resolve. */
  facetsUnsupported: boolean;
}

export const TICKET_FACETS_DEBOUNCE_MS = 300;

export function useTicketFacets(bucket: string, filters: TicketFacetFilters): TicketFacetsState {
  const [facets, setFacets] = useState<TicketFacets | null>(null);
  const [facetsUnsupported, setFacetsUnsupported] = useState(false);
  // The newest FIRED request's sequence number (incremented at fire time, not at effect time, so a
  // debounce-cancelled request never invalidates the one that actually went out).
  const latestSeq = useRef(0);
  // Read synchronously inside the effect — state alone would let the in-flight tail of the failing
  // request schedule one more fetch before the re-render lands.
  const unsupported = useRef(false);
  // Has ANY read ever landed? Separates "this backend has no facets route" from "one read failed".
  const everSucceeded = useRef(false);

  const requestKey = JSON.stringify({ bucket, ...filters });

  useEffect(() => {
    if (unsupported.current) return;
    const params = JSON.parse(requestKey) as Parameters<typeof getTicketFacets>[0];
    const id = setTimeout(() => {
      const seq = ++latestSeq.current;
      getTicketFacets(params).then(res => {
        if (seq !== latestSeq.current) return; // a newer request has already been fired
        if (res) {
          everSucceeded.current = true;
          setFacets(res);
          return;
        }
        // Only the never-succeeded case latches (see the constraints note) — `facets` is read off
        // a ref rather than the state value so this decision doesn't need the effect to re-run.
        if (everSucceeded.current) return;
        unsupported.current = true;
        setFacetsUnsupported(true);
      });
    }, TICKET_FACETS_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [requestKey]);

  return { facets, facetsUnsupported };
}
